import React, {useState, useEffect} from 'react';
import {Box, Flex, VStack, Wrap, WrapItem, Collapse, HStack, useToast} from '@chakra-ui/react';
import {TextG14} from '../../../components/typography';
import Modal from '../../../components/Modals/Modal';
import ToggleChip from '../../../components/ToggleChip';
import {useAvailability, useUpsertAvailability} from '../../../hooks/useAvailability';
import styled from 'styled-components';

const SaveButton = styled.button<{$disabled?: boolean}>`
  width: 100%;
  padding: 14px;
  border-radius: 28px;
  border: none;
  font-family: Gordita;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: ${(props) => (props.$disabled ? '#E5E5E5' : '#e02f84')};
  box-shadow: ${(props) => (props.$disabled ? 'none' : '0 -4px 0 0 rgba(0, 0, 0, 0.25) inset')};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.15s ease;
  &:active {
    transform: ${(props) => (props.$disabled ? 'none' : 'scale(0.98)')};
  }
`;

// Backend AvailabilitySlot enum values
const GENERAL_SLOTS = [
  {id: 'Morning', label: 'Mornings', emoji: '🌅'},
  {id: 'Afternoon', label: 'Afternoons', emoji: '✨'},
  {id: 'Evening', label: 'Evenings', emoji: '🏙️'},
];

// Backend Weekday enum values
const DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;
const DAY_TIMES = [
  {id: 'Morning', label: 'Morning', emoji: '🌤️'},
  {id: 'Afternoon', label: 'Afternoon', emoji: '✨'},
  {id: 'Evening', label: 'Evening', emoji: '🏙️'},
];

interface EditAvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EditAvailabilityModal = ({isOpen, onClose}: EditAvailabilityModalProps) => {
  const {data: availability} = useAvailability();
  const upsertMutation = useUpsertAvailability();
  const toast = useToast();

  const [selected, setSelected] = useState<string[]>([]);
  const [showSpecificDays, setShowSpecificDays] = useState(false);
  const [daySelections, setDaySelections] = useState<Record<string, string[]>>({});

  // Re-sync state when modal opens or data loads
  useEffect(() => {
    if (isOpen && availability) {
      setSelected(availability.general);
      setDaySelections(availability.specific);
      setShowSpecificDays(Object.keys(availability.specific).length > 0);
    }
  }, [isOpen, availability]);

  const toggleSlot = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const toggleDayTime = (day: string, timeId: string) => {
    setDaySelections((prev) => {
      const daySlots = prev[day] || [];
      const updated = daySlots.includes(timeId)
        ? daySlots.filter((s) => s !== timeId)
        : [...daySlots, timeId];
      const next = {...prev};
      if (updated.length === 0) {
        delete next[day];
      } else {
        next[day] = updated;
      }
      return next;
    });
  };

  const handleSave = () => {
    upsertMutation.mutate(
      {general: selected, specific: daySelections},
      {
        onSuccess: () => {
          toast({title: 'Availability updated!', status: 'success', duration: 2000});
          onClose();
        },
        onError: (err: any) => {
          const msg = err?.response?.data?.message || 'Could not update availability';
          toast({title: msg, status: 'error', duration: 3000});
        },
      },
    );
  };

  return (
    <Modal
      show={isOpen}
      parentCallback={() => onClose()}
      closeButton
      closeButtonPosition="right"
      header={
        <TextG14 fontWeight="700" textAlign="center" w="100%" mt="0">
          My Availability
        </TextG14>
      }
      modalPadding="16px 20px 24px"
      scrollBehavior="inside">
      <VStack spacing={5} align="stretch" justify="space-between">
        <Box>
          {/* Info box */}
          <Box
            bg="#F9F9F9"
            borderRadius="12px"
            p={4}
            mb={5}
            border="2px solid #E9E9E9"
            boxShadow="0 0 16px 0 rgba(0, 0, 0, 0.08)">
            <TextG14 mt="0" color="#1A1A1A" lineHeight="20px">
              Pick the times you're usually free. We'll use this to help match you with neighbors.
            </TextG14>
          </Box>

          {/* Time slots */}
          <TextG14 mt="0" fontWeight="700" mb={3}>
            I'm usually free during...
          </TextG14>

          <Wrap spacing={2}>
            {GENERAL_SLOTS.map((slot) => (
              <WrapItem key={slot.id}>
                <ToggleChip
                  label={slot.label}
                  emoji={slot.emoji}
                  isSelected={selected.includes(slot.id)}
                  onToggle={() => toggleSlot(slot.id)}
                />
              </WrapItem>
            ))}
          </Wrap>

          {/* Choose specific days link */}
          <Box mt={4}>
            <TextG14
              mt="0"
              color="#1A1A1A"
              textDecoration="underline"
              cursor="pointer"
              fontWeight="500"
              onClick={() => setShowSpecificDays((prev) => !prev)}>
              {showSpecificDays ? 'Hide specific days' : 'Choose specific days instead'}
            </TextG14>
          </Box>

          {/* Specific days section */}
          <Collapse in={showSpecificDays} animateOpacity>
            <VStack spacing={4} align="stretch" mt={5}>
              {DAYS.map((day) => (
                <Box key={day}>
                  <TextG14 mt="0" fontWeight="700" mb={2}>
                    {day}
                  </TextG14>
                  <HStack spacing={2}>
                    {DAY_TIMES.map((time) => (
                      <ToggleChip
                        key={`${day}_${time.id}`}
                        label={time.label}
                        emoji={time.emoji}
                        isSelected={(daySelections[day] || []).includes(time.id)}
                        onToggle={() => toggleDayTime(day, time.id)}
                      />
                    ))}
                  </HStack>
                </Box>
              ))}
            </VStack>
          </Collapse>
        </Box>

        <SaveButton $disabled={upsertMutation.isLoading} onClick={handleSave}>
          {upsertMutation.isLoading ? 'Saving...' : 'Save'}
        </SaveButton>
      </VStack>
    </Modal>
  );
};

export default EditAvailabilityModal;
